import {
  getInputFileFromRemote,
  getInputFileHeadersFromRemote,
  getInputFilesFromRemote,
  getLayout,
  sendMail,
} from '@/app/features/products/common/services';
import { modalUtils } from '@/app/core/utils';
import { FieldMappings } from '@/app/features/products/common/components/frontend-processing/models';
import { useCallback, useRef, useState, useEffect } from 'react';
import { Table } from 'antd';
import dayjs, { extend as dayJSExtend } from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import {
  HeaderInfo,
  InputRemoteFile,
  fetchAndEnforceApiGatewayResponseData,
  FileLayoutData,
  InputLabelsMap,
  InputRemoteFileVersion,
  isLayoutRequired,
} from '@ssar-app/domain';
import { Modal } from '@ssar-app/uikit';
import {
  Button,
  Grid,
  GridItem,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
} from '@chakra-ui/react';
import {
  AiOutlineReload,
  AiOutlineSearch,
  AiOutlineUpload,
} from 'react-icons/ai/index.js';

dayJSExtend(utcPlugin);

interface OneClickInputRemoteFileSelectorProps {
  subCode: string;
  isCompact?: boolean;
  beforeUpload: (
    headersInfo: HeaderInfo,
    file?: File,
    content?: string[],
    experianFileName?: string,
    clientFileName?: string,
    totalRecords?: number,
    intentType?: string,
    fileId?: string,
  ) => void;
  productName: string;
  onSuccess: (
    clientFileName: string,
    serverFileName: string,
    fieldMappings: FieldMappings[],
    hasHeaders?: boolean,
    recordsExpected?: number,
  ) => void;
  inputLabelsMap?: InputLabelsMap;
  isModalVisible: boolean;
  setIsModalVisible: (arg0: boolean) => void;
}

type OneClickInputRemoteFilesModel = InputRemoteFile & InputRemoteFileVersion;

export const OneClickInputRemoteFileSelector = (
  props: OneClickInputRemoteFileSelectorProps,
) => {
  const { isModalVisible, setIsModalVisible } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const remoteData = useRef<OneClickInputRemoteFilesModel[]>([]);
  const [tempRemoteData, setRemoteTempData] = useState<
    OneClickInputRemoteFilesModel[]
  >([]);
  const [fileSearchText, setFileSearchText] = useState<string>('');

  const columns = [
    {
      title: 'Client File Name',
      dataIndex: 'fileName',
      sorter: (
        a: OneClickInputRemoteFilesModel,
        b: OneClickInputRemoteFilesModel,
      ) => a.fileName.localeCompare(b.fileName),
      render: (text: string, row: OneClickInputRemoteFilesModel) => (
        <SelectButton row={row}></SelectButton>
      ),
    },
    /* {
      title: 'Experian Name',
      dataIndex: 'ExperianInputFileName',
      sorter: true,
    }, */
    {
      title: 'File Size',
      dataIndex: 'fileSizeMb',
      render: (size: number) => {
        if (size === 0) {
          return '0 Bytes';
        }
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const bytes = size * Math.pow(1024, 2);
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return (bytes / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i];
      },
      sorter: (
        a: OneClickInputRemoteFilesModel,
        b: OneClickInputRemoteFilesModel,
      ) => (b?.fileSizeMb || 0) - (a?.fileSizeMb || 0),
    },
    {
      title: 'Total Records',
      dataIndex: 'totalRecords',
      sorter: (
        a: OneClickInputRemoteFilesModel,
        b: OneClickInputRemoteFilesModel,
      ) => (b?.totalRecords || 0) - (a?.totalRecords || 0),
    },
    /* {
      title: 'Uploaded Date',
      dataIndex: 'TimeUploaded',
      render: (text: number) => {
        return dayjs.unix(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ');
      },
      sorter: (a: OneClickInputRemoteFilesModel, b: OneClickInputRemoteFilesModel) =>
        dayjs.unix(b.TimeUploaded).diff(dayjs.unix(a.TimeUploaded)),
    }, */
    {
      title: 'Uploaded Date',
      dataIndex: 'landingZoneFileUploadedTime',
      render: (text: number) => {
        return dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ');
      },
      sorter: (
        a: OneClickInputRemoteFilesModel,
        b: OneClickInputRemoteFilesModel,
      ) =>
        dayjs(b.landingZoneFileUploadedTime).diff(
          dayjs(a.landingZoneFileUploadedTime),
        ),
    },
    /* {
      title: 'Type',
      dataIndex: 'FileType',
      sorter: true,
    },
      title: 'Client ID',
      dataIndex: 'ClientId',
      sorter: true,
    }, */
    /* {
      title: 'Select',
      key: 'select',
      render: (text: string, row: OneClickInputRemoteFilesModel) => (
        <SelectButton row={row}></SelectButton>
      ),
    }, */
  ];

  const getData = (): void => {
    setIsLoading(true);
    setFileSearchText('');
    fetchAndEnforceApiGatewayResponseData(() =>
      getInputFilesFromRemote(props.subCode, props.productName),
    )
      .then((data) => {
        const newData = data?.map((item) => {
          const file: OneClickInputRemoteFilesModel = {
            clientKey: item.clientKey,
            fileType: item.fileType,
            fileName: item.fileName,
            intentType: item.intentType,
            productName: item.productName,
            errors: item.errors,
            totalRecords: item.versions[0].totalRecords,
            landingZoneFileUploadedTime:
              item.versions[0].landingZoneFileUploadedTime,
            fileSizeMb: item.versions[0].fileSizeMb,
            fileId: item.versions[0].fileId,
            timeUploaded: item.versions[0].totalRecords,
            hasHeaders: item.versions[0].hasHeaders,
            versions: [],
          };
          return file;
        });
        remoteData.current = newData || [];
        setRemoteTempData(newData || []);
      })
      .catch((error) => {
        modalUtils.showError(error, 'Error while getting input files.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getDataWrapper = useCallback(() => getData(), []);

  useEffect(() => {
    if (isModalVisible) {
      getDataWrapper();
    }
  }, [isModalVisible, getDataWrapper]);

  useEffect(() => {
    setPageNo(1);
    setRemoteTempData(
      remoteData.current.filter((file) =>
        file.fileName.toLowerCase().includes(fileSearchText.toLowerCase()),
      ),
    );
  }, [fileSearchText]);

  interface SelectButtonProps {
    row: OneClickInputRemoteFilesModel;
  }

  const getFieldMappings = (LayoutData: FileLayoutData): FieldMappings[] => {
    return LayoutData.layout.reduce((acc, field, index) => {
      const value = !field?.experianHeader ? undefined : field?.experianHeader;
      const effectiveHeader = value || field.clientHeader;
      const recordIndex = acc.findIndex(
        (a) => a.field === (field.experianHeader || index.toString()),
      );
      const toInsert: FieldMappings = {
        field: field.clientHeader || index.toString(),
        headerNameWithLength: {
          name: value,
          fieldLength: props.inputLabelsMap
            ? props.inputLabelsMap[effectiveHeader.toLowerCase()]
                ?.fieldLength || 50
            : 50,
        },
      };
      if (recordIndex === -1) {
        acc.push(toInsert);
      } else {
        acc[recordIndex] = toInsert;
      }
      return acc;
    }, [] as FieldMappings[]);
  };

  const SelectButton = (buttonProps: SelectButtonProps) => {
    const { row } = buttonProps;
    const selectClicked = (): void => {
      setIsLoading(true);
      if (row.errors && row.errors.length !== 0) {
        const errorDetails: Record<string, string> = {
          ...row.errors[0],
          fileName: row.fileName,
          fileType: 'Independently Uploaded',
        };
        sendMail('ML-Failure', errorDetails).then((_res) => {
          // console.log(res);
        });
      }
      Promise.all([
        getInputFileFromRemote(row.fileId, 100, true),
        getInputFileHeadersFromRemote(row.fileId),
      ])

        .then(([contentResponse, headersResponse]) => {
          props.beforeUpload(
            headersResponse.data?.headersInfo as HeaderInfo,
            undefined,
            contentResponse?.data || [],
            row.fileName,
            row.fileName,
            row.totalRecords,
            row.intentType,
            row.fileId,
          );
          setIsLoading(false);
          setIsModalVisible(false);
        })
        .catch((error) => {
          modalUtils.showError(error, 'Error while getting input file.');
          setIsLoading(false);
        });
    };
    const isLayoutAvailable = (): void => {
      setIsLoading(true);
      getLayout(row.fileId)
        .then((FileLayoutData) => {
          if (FileLayoutData.status !== 'Success') {
            selectClicked();
          } else {
            props.onSuccess(
              row.fileName,
              row.fileName,
              getFieldMappings(FileLayoutData.data as FileLayoutData),
              row.hasHeaders as boolean,
              row.totalRecords,
            );
            setIsModalVisible(false);
          }
        })
        .catch((error) => {
          if (error.message === 'Could not get File Layout') {
            selectClicked();
          } else {
            setIsLoading(false);
            modalUtils.showError(error, 'Error while getting Layout.');
          }
        });
    };

    return (
      <a
        onClick={(e) => {
          e.preventDefault();
          if (isLayoutRequired.getLayout) {
            isLayoutAvailable();
          } else {
            selectClicked();
          }
        }}
        href='/'
      >
        {row.fileName}
      </a>
    );
  };

  return (
    <>
      {props.isCompact && (
        <Button
          colorScheme='gray'
          variant='outline'
          leftIcon={<AiOutlineUpload />}
          onClick={() => setIsModalVisible(true)}
          ml={2.5}
          height='8'
        >
          Select file already transferred to Experian
        </Button>
      )}
      {!props.isCompact && (
        <Button
          colorScheme='gray'
          variant='outline'
          leftIcon={<AiOutlineUpload />}
          onClick={() => setIsModalVisible(true)}
          w={96}
          height='10'
          border='1px solid'
          borderColor={'gray.300'}
        >
          Select file already transferred to Experian
        </Button>
      )}
      {isModalVisible && (
        <Modal
          header={
            <Grid alignItems='center' templateColumns='repeat(12, 1fr)' mr={9}>
              <GridItem colSpan={6}>
                Select a file already transferred to Experian
              </GridItem>
              <GridItem
                colSpan={6}
                ml='auto'
                display='flex'
                alignItems='center'
                flexDirection='row'
              >
                <InputGroup size='sm'>
                  <Input
                    value={fileSearchText}
                    disabled={isLoading}
                    placeholder='Search Files by Name'
                    isInvalid={false}
                    onChange={(e) =>
                      setFileSearchText(e.target.value.trim().toLowerCase())
                    }
                    spellCheck={false}
                  />
                  <InputRightAddon>
                    <Icon as={AiOutlineSearch} />
                  </InputRightAddon>
                </InputGroup>
                <Icon
                  as={AiOutlineReload}
                  onClick={getData}
                  float='right'
                  ml={4}
                />
              </GridItem>
            </Grid>
          }
          headerProps={{ py: 2 }}
          isOpen
          contentProps={{ maxW: '95vw' }}
          bodyProps={{ px: 4 }}
          onClose={() => {
            setIsLoading(false);
            setIsModalVisible(false);
          }}
          showOk={false}
          scrollBehavior='outside'
          closeOnOverlayClick={false}
        >
          <Table
            style={{ width: '100%' }}
            loading={isLoading}
            columns={columns}
            dataSource={tempRemoteData}
            // scroll={{ scrollToFirstRowOnChange: true, y: 300 }}
            size={'small'}
            pagination={{
              current: pageNo,
              onChange: (page) => setPageNo(page),
              total: tempRemoteData.length,
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} items`,
              position: ['bottomLeft'],
              showSizeChanger: true,
              simple: false,
            }}
          ></Table>
        </Modal>
      )}
    </>
  );
};
